import { useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { hole } from '../lib/api'
import { mitgliedsFarbe } from '../session/logic'
import { Avatar, Card, Empty, ListItem, SegmentedButtons } from '../ui'

interface MemberCount {
  memberId: string
  displayName: string
  role?: string
  gesamt?: number
  werteJeWoche?: number[]
}

interface WochenStatistik {
  kw: string
  label: string
  von: string
  bis: string
}

interface StatsResponse {
  wochen?: WochenStatistik[]
  gesamtErledigt?: number
  nachArt?: {
    todo?: number
    chore?: number
  }
  nachMitglied?: MemberCount[]
  wochenSummen?: number[]
}

type ZeitraumOption = '4' | '8' | '12'

export function Statistiken() {
  const [zeitraum, setZeitraum] = useState<ZeitraumOption>('4')

  const { data, isPending } = useQuery({
    queryKey: ['tasks', 'stats', zeitraum],
    queryFn: () => hole<StatsResponse>(`/api/tasks/stats?wochen=${zeitraum}`),
  })

  // Die API liefert Wochen, Summen und Mitglieder getrennt. Alle Zugriffe bleiben
  // defensiv, damit auch eine ältere, bereits im Browser gecachte Antwort nicht abstürzt.
  const wochen = Array.isArray(data?.wochen) ? data.wochen : []
  const wochenSummen = wochen.map((_, index) => data?.wochenSummen?.[index] ?? 0)
  const summeGesamt = data?.gesamtErledigt ?? wochenSummen.reduce((acc, wert) => acc + wert, 0)
  const summeChores = data?.nachArt?.chore ?? 0
  const summeTodos = data?.nachArt?.todo ?? 0
  const personenListe = (Array.isArray(data?.nachMitglied) ? data.nachMitglied : [])
    .map((person) => ({
      ...person,
      gesamt: person.gesamt ?? 0,
      werteJeWoche: Array.isArray(person.werteJeWoche) ? person.werteJeWoche : [],
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'))

  // SVG Chart Maße
  const chartWidth = 600
  const chartHeight = 260
  const padLeft = 40
  const padRight = 20
  const padTop = 20
  const padBottom = 40
  const plotWidth = chartWidth - padLeft - padRight
  const plotHeight = chartHeight - padTop - padBottom

  const maxWert = Math.max(5, ...wochenSummen)
  const ySkala = (wert: number) => padTop + plotHeight - (wert / maxWert) * plotHeight

  // Raster-Stufen
  const yTicks = [0, Math.ceil(maxWert / 2), maxWert]

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Aufgaben-Statistiken</h1>
          <p>Neutrale Übersicht erledigter Haushalts- und persönlicher Aufgaben.</p>
        </div>
        <div>
          <SegmentedButtons<ZeitraumOption>
            label="Zeitraum wählen"
            wert={zeitraum}
            optionen={[
              { wert: '4', label: '4 Wochen' },
              { wert: '8', label: '8 Wochen' },
              { wert: '12', label: '12 Wochen' },
            ]}
            onWert={(w) => setZeitraum(w)}
          />
        </div>
      </div>

      {isPending ? (
        <p className="unterzeile">Statistiken werden geladen …</p>
      ) : (
        <div className="stapel">
          {/* Kennzahlen-Karten */}
          <div className="kachel-raster">
            <Card titel="Gesamt erledigt">
              <span className="statistiken-wert">{summeGesamt}</span>
              <p className="unterzeile">Erledigungen im Zeitraum</p>
            </Card>
            <Card titel="Haushaltsaufgaben">
              <span className="statistiken-wert">{summeChores}</span>
              <p className="unterzeile">Gemeinsame Aufgaben</p>
            </Card>
            <Card titel="Persönliche Aufgaben">
              <span className="statistiken-wert">{summeTodos}</span>
              <p className="unterzeile">Eigene To-Dos</p>
            </Card>
          </div>

          {/* SVG Diagramm */}
          <Card titel="Wöchentliche Erledigungen nach Person">
            {summeGesamt === 0 ? (
              <Empty
                titel="Keine Erledigungen im Zeitraum"
                text="In den ausgewählten Wochen wurden noch keine Aufgaben als erledigt markiert."
              />
            ) : (
              <div>
                <div className="statistiken-diagramm-huelle">
                  <svg
                    role="img"
                    aria-label={`Wöchentliche Aufgaben-Erledigungen der letzten ${zeitraum} Wochen`}
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    className="statistiken-diagramm-svg"
                  >
                    <title>Wöchentliche Erledigungen</title>
                    <desc>
                      Gestapeltes Balkendiagramm der erledigten Aufgaben je Woche und
                      Familienmitglied.
                    </desc>

                    {/* Y-Achsen-Rasterlinien */}
                    {yTicks.map((tick) => {
                      const y = ySkala(tick)
                      return (
                        <g key={tick}>
                          <line
                            x1={padLeft}
                            y1={y}
                            x2={chartWidth - padRight}
                            y2={y}
                            stroke="var(--c-outline-variant)"
                            strokeDasharray="4 4"
                          />
                          <text
                            x={padLeft - 8}
                            y={y + 4}
                            textAnchor="end"
                            fill="var(--c-on-surface-variant)"
                            fontSize="11"
                          >
                            {tick}
                          </text>
                        </g>
                      )
                    })}

                    {/* Wochen-Balken (gestapelt je Person) */}
                    {wochen.map((w, idx) => {
                      const bandBreite = plotWidth / wochen.length
                      const balkenBreite = Math.min(32, bandBreite * 0.6)
                      const x = padLeft + idx * bandBreite + (bandBreite - balkenBreite) / 2
                      const centerX = padLeft + (idx + 0.5) * bandBreite

                      let aktuellesY = padTop + plotHeight

                      return (
                        <g key={w.label}>
                          {/* Segmente für jede Person in dieser Woche */}
                          {personenListe.map((m) => {
                            const wochenWert = m.werteJeWoche[idx] ?? 0
                            if (wochenWert === 0) return null
                            const segmentHoehe = (wochenWert / maxWert) * plotHeight
                            aktuellesY -= segmentHoehe
                            const farbe = mitgliedsFarbe(null, m.memberId)

                            return (
                              <rect
                                key={m.memberId}
                                x={x}
                                y={aktuellesY}
                                width={balkenBreite}
                                height={Math.max(1, segmentHoehe)}
                                fill={farbe}
                                rx="2"
                              >
                                <title>{`${m.displayName}: ${wochenWert} erledigt in ${w.kw}`}</title>
                              </rect>
                            )
                          })}

                          {/* Wochen-Beschriftung */}
                          <text
                            x={centerX}
                            y={chartHeight - padBottom + 20}
                            textAnchor="middle"
                            fill="var(--c-on-surface-variant)"
                            fontSize="11"
                          >
                            {w.kw}
                          </text>
                        </g>
                      )
                    })}
                  </svg>
                </div>

                {/* Barrierefreie Datentabelle für Screenreader */}
                <table className="sr-only">
                  <caption>Erledigte Aufgaben je Woche und Person</caption>
                  <thead>
                    <tr>
                      <th scope="col">Woche</th>
                      <th scope="col">Gesamt</th>
                      {personenListe.map((p) => (
                        <th key={p.memberId} scope="col">
                          {p.displayName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {wochen.map((w, idx) => (
                      <tr key={w.label}>
                        <th scope="row">{w.label}</th>
                        <td>{wochenSummen[idx] ?? 0}</td>
                        {personenListe.map((p) => {
                          return <td key={p.memberId}>{p.werteJeWoche[idx] ?? 0}</td>
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Legende */}
                <div className="statistiken-legende">
                  {personenListe.map((p) => (
                    <div key={p.memberId} className="statistiken-legende-eintrag">
                      <span
                        className="statistiken-legende-punkt"
                        style={{ '--stripe': mitgliedsFarbe(null, p.memberId) } as CSSProperties}
                      />
                      <span>{p.displayName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Übersicht nach Person */}
          <section aria-labelledby="personen-titel">
            <h2 id="personen-titel" className="typ-titel-md">
              Erledigungen nach Person
            </h2>
            {personenListe.length === 0 ? (
              <Empty
                titel="Noch keine Erledigungen"
                text="Sobald Aufgaben abgehakt werden, erscheinen sie hier nach Person aufgeschlüsselt."
              />
            ) : (
              <ul className="liste liste--gruppe" aria-label="Erledigte Aufgaben nach Person">
                {personenListe.map((p) => {
                  const farbe = mitgliedsFarbe(null, p.memberId)
                  return (
                    <li key={p.memberId}>
                      <ListItem
                        as="div"
                        titel={p.displayName}
                        unterzeile="Im ausgewählten Zeitraum"
                        fuehrend={<Avatar name={p.displayName} farbe={farbe} />}
                        folgend={
                          <span className="statistiken-person-anzahl">{p.gesamt} erledigt</span>
                        }
                      />
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </div>
      )}
    </>
  )
}
